import { _, api, config, hooks, mui, React, ts } from '_core';

import CreatePortfolio from './create-portfolio';
import { DraftParams, Params } from './types';

type FormProps = {
  params: DraftParams;
  setParams: (_p: Params) => void;
};

const Form = ({ params, setParams }: FormProps) => {
  const uiStyles = hooks.useUiStyles();
  const isPortfoliosEnabled = config.features.portfolios_product_enabled;

  const [syncWithPortfolios, setSyncWithPortfolios] = React.useState(params.sync_with_portfolios_app || false);
  const [selectedPortfolioId, setSelectedPortfolioId] = React.useState<number | undefined>(params.app_portfolio_id);
  const [portfolios, setPortfolios] = React.useState<any[]>([]);
  const [loadingPortfolios, setLoadingPortfolios] = React.useState(true);
  const [showCreatePortfolio, setShowCreatePortfolio] = React.useState(false);
  const [portfolioMode, setPortfolioMode] = React.useState<'existing' | 'new'>('existing');
  const [pointInTimeTickers, setPointInTimeTickers] = React.useState(params.point_in_time_tickers ?? false);

  // Disable sync if portfolios are not enabled
  React.useEffect(() => {
    if (!isPortfoliosEnabled && syncWithPortfolios) {
      setSyncWithPortfolios(false);
    }
  }, [isPortfoliosEnabled, syncWithPortfolios]);

  // Load portfolios when sync flag is enabled
  React.useEffect(() => {
    const loadPortfolios = async () => {
      // Just load portfolios if they are not already loaded
      if (syncWithPortfolios && isPortfoliosEnabled && _.isEmpty(portfolios)) {
        setLoadingPortfolios(true);
        try {
          const response = await api.app_portfolios.search({
            order_by: [{ column: 'created_at', sort: 'desc' }],
            query: ['archived', '=', 'false'],
          });
          setPortfolios(response.data);
        } catch (error) {
          console.error('Error loading portfolios:', error);
        } finally {
          setLoadingPortfolios(false);
        }
      } else {
        setSelectedPortfolioId(undefined);
      }
    };

    loadPortfolios();
  }, [syncWithPortfolios, isPortfoliosEnabled]);

  // Handle portfolio creation
  const handlePortfolioCreated = (newPortfolio: ts.types.appPortfolio.Portfolio) => {
    setPortfolios((prev) => [newPortfolio, ...prev]);
    setSelectedPortfolioId(newPortfolio.id);
    setPortfolioMode('existing'); // Switch to existing mode to show the newly created portfolio
    setShowCreatePortfolio(false);
  };

  React.useEffect(() => {
    if (portfolios.length === 0 && portfolioMode === 'existing' && !loadingPortfolios && syncWithPortfolios) {
      setPortfolioMode('new');
    }
  }, [portfolios.length, portfolioMode, loadingPortfolios, syncWithPortfolios]);

  // Update params when form values change
  React.useEffect(() => {
    const data = {
      ...params,
      sync_with_portfolios_app: syncWithPortfolios,
      app_portfolio_id: selectedPortfolioId,
      point_in_time_tickers: syncWithPortfolios ? false : pointInTimeTickers,
    };
    setParams(data);
  }, [syncWithPortfolios, selectedPortfolioId, pointInTimeTickers]);

  return (
    <mui.core.Box sx={uiStyles.widgetEditMode}>
      <mui.core.Card variant="outlined" sx={{ p: 3, mt: 2 }}>
        <mui.core.Box display="flex" alignItems="center" justifyContent="space-between">
          <mui.core.Box>
            <mui.core.Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
              Point in Time Tickers
            </mui.core.Typography>
            <mui.core.Typography variant="body2" color="textSecondary">
              If enabled, the tickers will be synced at the point in time of the report generation.
            </mui.core.Typography>
          </mui.core.Box>
          <mui.core.Switch
            checked={pointInTimeTickers}
            onChange={(e) => setPointInTimeTickers(e.target.checked)}
            color="primary"
            size="medium"
          />
        </mui.core.Box>
      </mui.core.Card>
      <mui.core.Card variant="outlined" sx={{ p: 3, mt: 2 }}>
        <mui.core.Box display="flex" alignItems="center" justifyContent="space-between">
          <mui.core.Box>
            <mui.core.Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
              Portfolios Integration
            </mui.core.Typography>
            <mui.core.Typography variant="body2" color="textSecondary">
              {isPortfoliosEnabled
                ? 'Sync this data with the Portfolios App for further analysis'
                : 'Portfolios App integration is not available'}
            </mui.core.Typography>
          </mui.core.Box>
          <mui.core.Switch
            checked={syncWithPortfolios}
            onChange={(e) => setSyncWithPortfolios(e.target.checked)}
            color="primary"
            disabled={!isPortfoliosEnabled}
            size="medium"
          />
        </mui.core.Box>
        {!isPortfoliosEnabled && (
          <mui.core.Typography variant="caption" color="textSecondary" display="block" mt={2}>
            Contact your administrator to enable Portfolios App integration
          </mui.core.Typography>
        )}
      </mui.core.Card>

      {syncWithPortfolios && isPortfoliosEnabled && (
        <mui.core.Box mt={3}>
          <mui.core.Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
            Portfolio Selection
          </mui.core.Typography>
          <mui.core.Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Choose an existing portfolio or create a new one to sync your data with the Portfolios App.
          </mui.core.Typography>

          {loadingPortfolios ? (
            <mui.core.Box display="flex" alignItems="center" gap={1} sx={{ py: 4 }}>
              <mui.core.CircularProgress size={20} />
              <mui.core.Typography variant="body2">Loading portfolios...</mui.core.Typography>
            </mui.core.Box>
          ) : (
            <>
              <mui.core.FormControl component="fieldset" sx={{ mb: 3 }}>
                <mui.core.RadioGroup
                  value={portfolioMode}
                  onChange={(e) => {
                    setPortfolioMode(e.target.value as typeof portfolioMode);
                    if (e.target.value === 'new') {
                      setSelectedPortfolioId(undefined);
                    }
                  }}
                  sx={{ gap: 1 }}
                >
                  <mui.core.FormControlLabel
                    value="existing"
                    control={<mui.core.Radio />}
                    label={
                      <mui.core.Box>
                        <mui.core.Typography variant="body2" fontWeight={500}>
                          Select existing portfolio
                        </mui.core.Typography>
                        <mui.core.Typography variant="caption" color="textSecondary">
                          Choose from your existing portfolios
                        </mui.core.Typography>
                      </mui.core.Box>
                    }
                  />
                  <mui.core.FormControlLabel
                    value="new"
                    control={<mui.core.Radio />}
                    label={
                      <mui.core.Box>
                        <mui.core.Typography variant="body2" fontWeight={500}>
                          Create new portfolio
                        </mui.core.Typography>
                        <mui.core.Typography variant="caption" color="textSecondary">
                          Create a new portfolio with this data
                        </mui.core.Typography>
                      </mui.core.Box>
                    }
                  />
                </mui.core.RadioGroup>
              </mui.core.FormControl>

              {portfolioMode === 'existing' && (
                <mui.core.Card variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                  <mui.core.Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                    Choose Portfolio
                  </mui.core.Typography>

                  {portfolios.length === 0 ? (
                    <mui.core.Box sx={{ py: 2, textAlign: 'center' }}>
                      <mui.core.Typography variant="body2" color="textSecondary">
                        No portfolios found. Create a new one instead.
                      </mui.core.Typography>
                      <mui.core.Button
                        variant="text"
                        size="small"
                        onClick={() => setPortfolioMode('new')}
                        sx={{ mt: 1 }}
                      >
                        Switch to create new portfolio
                      </mui.core.Button>
                    </mui.core.Box>
                  ) : (
                    <mui.core.FormControl fullWidth variant="outlined" size="small">
                      <mui.core.InputLabel>Select Portfolio</mui.core.InputLabel>
                      <mui.core.Select
                        value={selectedPortfolioId || ''}
                        onChange={(e) => setSelectedPortfolioId(Number(e.target.value) || undefined)}
                        label="Select Portfolio"
                      >
                        {portfolios.map((portfolio) => (
                          <mui.core.MenuItem key={portfolio.id} value={portfolio.id}>
                            <mui.core.Box>
                              <mui.core.Typography variant="body2">{portfolio.name}</mui.core.Typography>
                              <mui.core.Typography variant="caption" color="textSecondary">
                                Created {new Date(portfolio.created_at).toLocaleDateString()}
                              </mui.core.Typography>
                            </mui.core.Box>
                          </mui.core.MenuItem>
                        ))}
                      </mui.core.Select>
                    </mui.core.FormControl>
                  )}
                </mui.core.Card>
              )}

              {portfolioMode === 'new' && (
                <mui.core.Card variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                  <mui.core.Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                    Create New Portfolio
                  </mui.core.Typography>
                  <mui.core.Button
                    variant="contained"
                    color="primary"
                    onClick={() => setShowCreatePortfolio(true)}
                    fullWidth
                    startIcon={<mui.icons.Add />}
                  >
                    Create Portfolio
                  </mui.core.Button>
                  <mui.core.Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    A new portfolio will be created and automatically selected
                  </mui.core.Typography>
                </mui.core.Card>
              )}
            </>
          )}
        </mui.core.Box>
      )}

      {isPortfoliosEnabled && (
        <CreatePortfolio
          open={showCreatePortfolio}
          setOpen={setShowCreatePortfolio}
          onPortfolioCreated={handlePortfolioCreated}
        />
      )}
    </mui.core.Box>
  );
};

export default Form;
